import { useEffect, useState } from "react";
import { useApp } from "@/contexts/AppContext";
import { useAuth } from "@/contexts/AuthContext";
import { ticketService } from "@/services/ticket.service";
import { markTicketsAsSeen } from "@/app/hooks/useUnreadTickets";
import type {
  TicketDetail,
  TicketMessage,
  TicketStatus,
  TicketSummary,
  TicketType,
} from "@/types/ticket.types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { TicketAttachmentPreview } from "@/components/tickets/TicketAttachmentPreview";
import { localizeTicketMessageText } from "@/components/tickets/ticketMessageLocalization";
import { MessageSquareWarning, Bug, Lightbulb, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 25;
const MAX_FILES = 5;
const MESSAGE_COLLAPSE_LENGTH = 320;
const MESSAGE_BUBBLE_MAX_WIDTH = "min(85%, 38rem)";
const STATUS_FILTERS: Array<TicketStatus | "ALL"> = ["ALL", "OPEN", "IN_PROGRESS", "CLOSED"];

type ReplyDraft = {
  message: string;
  attachments: File[];
};

const EMPTY_REPLY_DRAFT: ReplyDraft = {
  message: "",
  attachments: [],
};

const toAttachmentKey = (file: File) =>
  `${file.name}:${file.size}:${file.lastModified}`;

const mergeAttachments = (current: File[], incoming: File[], maxFiles: number) => {
  const seen = new Set(current.map(toAttachmentKey));
  const merged = [...current];
  for (const file of incoming) {
    const key = toAttachmentKey(file);
    if (seen.has(key)) continue;
    merged.push(file);
    seen.add(key);
  }
  return merged.slice(0, maxFiles);
};

const formatBytes = (value: number) => {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(2)} MB`;
};

const statusBadgeVariant = (status: TicketStatus): "default" | "secondary" | "outline" => {
  if (status === "OPEN") return "default";
  if (status === "IN_PROGRESS") return "secondary";
  return "outline";
};

const ticketTypeBadgeClass = (type: TicketType) => {
  if (type === "BUG") return "border-destructive/40 bg-destructive/10 text-destructive";
  if (type === "FEATURE") return "border-primary/40 bg-primary/10 text-primary";
  return "border-secondary/40 bg-secondary/10 text-secondary-foreground";
};

const ticketTypeIcon = (type: TicketType) => {
  if (type === "BUG") return Bug;
  if (type === "FEATURE") return Lightbulb;
  return CheckCircle2;
};
const TICKET_META_BADGE_CLASS = "inline-flex h-9 items-center px-3 text-sm";

const canReplyToTicket = (ticket: TicketDetail) =>
  ticket.status !== "CLOSED" || !!ticket.reopenUntil;

const canCloseTicket = (ticket: TicketDetail) =>
  ticket.status === "OPEN" || ticket.status === "IN_PROGRESS";

const keepScrollPositionAfterFileSelection = () => {
  if (typeof window === "undefined") return;
  const left = window.scrollX;
  const top = window.scrollY;
  window.requestAnimationFrame(() => {
    window.scrollTo({ left, top, behavior: "auto" });
  });
};

interface TicketSettingsProps {
  navigate: (page: string) => void;
}

export function TicketSettings({ navigate }: TicketSettingsProps) {
  const { t } = useApp();
  const { user } = useAuth();
  const isAdmin = (user?.role ?? "").toUpperCase() === "ADMIN";
  const iconStyle = {
    color:
      "color-mix(in srgb, color-mix(in srgb, var(--primary) 70%, var(--accent)) 90%, var(--foreground))",
  } as const;
  const primaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 82%, transparent)",
  } as const;
  const secondaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 78%, transparent)",
  } as const;

  const [tickets, setTickets] = useState<TicketSummary[]>([]);
  const [statusFilter, setStatusFilter] = useState<TicketStatus | "ALL">("ALL");
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [loadingDetailIds, setLoadingDetailIds] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [expandedMessageIds, setExpandedMessageIds] = useState<Set<string>>(new Set());
  const [expandedTicketIds, setExpandedTicketIds] = useState<Set<string>>(new Set());
  const [ticketDetailsById, setTicketDetailsById] = useState<Record<string, TicketDetail>>({});
  const [replyDraftsByTicketId, setReplyDraftsByTicketId] = useState<Record<string, ReplyDraft>>({});
  const [isCreateExpanded, setIsCreateExpanded] = useState(false);

  const [createTitle, setCreateTitle] = useState("");
  const [createType, setCreateType] = useState<TicketType>("BUG");
  const [createMessage, setCreateMessage] = useState("");
  const [createAttachments, setCreateAttachments] = useState<File[]>([]);

  const isOwnMessage = (message: TicketMessage) => {
    if (user?.id && message.senderId === user.id) {
      return true;
    }
    return message.senderType === "USER";
  };

  const isSystemMessage = (message: TicketMessage) => message.senderType === "SYSTEM";

  const getReplyDraft = (ticketId: string): ReplyDraft =>
    replyDraftsByTicketId[ticketId] ?? EMPTY_REPLY_DRAFT;

  const toggleMessageExpanded = (messageId: string) => {
    setExpandedMessageIds((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) next.delete(messageId);
      else next.add(messageId);
      return next;
    });
  };

  const toCollapsedMessage = (value: string) => {
    if (value.length <= MESSAGE_COLLAPSE_LENGTH) return value;
    return `${value.slice(0, MESSAGE_COLLAPSE_LENGTH).trimEnd()}...`;
  };

  const loadTicketDetail = async (ticketId: string) => {
    setLoadingDetailIds((prev) => {
      const next = new Set(prev);
      next.add(ticketId);
      return next;
    });
    try {
      const detail = await ticketService.getMyTicket(ticketId);
      setTicketDetailsById((prev) => ({
        ...prev,
        [ticketId]: detail,
      }));
    } catch (error: any) {
      toast.error(error?.response?.data?.message ?? t("common.genericError"));
    } finally {
      setLoadingDetailIds((prev) => {
        const next = new Set(prev);
        next.delete(ticketId);
        return next;
      });
    }
  };

  const loadTickets = async (status: TicketStatus | "ALL") => {
    setIsLoadingList(true);
    try {
      const response = await ticketService.getMyTickets({
        page: 0,
        size: PAGE_SIZE,
        status: status === "ALL" ? undefined : status,
      });
      markTicketsAsSeen(response.items);
      setTickets(response.items);

      const ticketIds = new Set(response.items.map((ticket) => ticket.id));

      setExpandedTicketIds((prev) => {
        const next = new Set<string>();
        for (const ticket of response.items) {
          if (prev.has(ticket.id) || ticket.status !== "CLOSED") {
            next.add(ticket.id);
          }
        }
        return next;
      });

      setTicketDetailsById((prev) => {
        const next: Record<string, TicketDetail> = {};
        Object.entries(prev).forEach(([ticketId, detail]) => {
          if (ticketIds.has(ticketId)) {
            next[ticketId] = detail;
          }
        });
        return next;
      });

      setReplyDraftsByTicketId((prev) => {
        const next: Record<string, ReplyDraft> = {};
        Object.entries(prev).forEach(([ticketId, draft]) => {
          if (ticketIds.has(ticketId)) {
            next[ticketId] = draft;
          }
        });
        return next;
      });
    } catch (error: any) {
      toast.error(error?.response?.data?.message ?? t("common.genericError"));
    } finally {
      setIsLoadingList(false);
    }
  };

  useEffect(() => {
    void loadTickets(statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    tickets.forEach((ticket) => {
      if (!expandedTicketIds.has(ticket.id)) return;
      if (ticketDetailsById[ticket.id]) return;
      if (loadingDetailIds.has(ticket.id)) return;
      void loadTicketDetail(ticket.id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets, expandedTicketIds, ticketDetailsById, loadingDetailIds]);

  const handleCreateAttachments = (files: FileList | null) => {
    if (!files) return;
    const next = Array.from(files);
    setCreateAttachments((prev) => {
      const merged = mergeAttachments(prev, next, MAX_FILES);
      if (prev.length + next.length > MAX_FILES) {
        toast.error(t("account.ticketAttachmentsLimit"));
      }
      return merged;
    });
  };

  const handleReplyAttachments = (ticketId: string, files: FileList | null) => {
    if (!files) return;
    const next = Array.from(files);
    setReplyDraftsByTicketId((prev) => {
      const current = prev[ticketId] ?? EMPTY_REPLY_DRAFT;
      const merged = mergeAttachments(current.attachments, next, MAX_FILES);
      if (current.attachments.length + next.length > MAX_FILES) {
        toast.error(t("account.ticketAttachmentsLimit"));
      }
      return {
        ...prev,
        [ticketId]: {
          ...current,
          attachments: merged,
        },
      };
    });
  };

  const setReplyMessage = (ticketId: string, message: string) => {
    setReplyDraftsByTicketId((prev) => {
      const current = prev[ticketId] ?? EMPTY_REPLY_DRAFT;
      return {
        ...prev,
        [ticketId]: {
          ...current,
          message,
        },
      };
    });
  };

  const removeCreateAttachment = (index: number) => {
    setCreateAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const clearCreateAttachments = () => {
    setCreateAttachments([]);
  };

  const removeReplyAttachment = (ticketId: string, index: number) => {
    setReplyDraftsByTicketId((prev) => {
      const current = prev[ticketId] ?? EMPTY_REPLY_DRAFT;
      return {
        ...prev,
        [ticketId]: {
          ...current,
          attachments: current.attachments.filter((_, i) => i !== index),
        },
      };
    });
  };

  const clearReplyAttachments = (ticketId: string) => {
    setReplyDraftsByTicketId((prev) => {
      const current = prev[ticketId] ?? EMPTY_REPLY_DRAFT;
      return {
        ...prev,
        [ticketId]: {
          ...current,
          attachments: [],
        },
      };
    });
  };

  const refreshTicket = async (ticketId: string) => {
    await loadTickets(statusFilter);
    if (expandedTicketIds.has(ticketId)) {
      await loadTicketDetail(ticketId);
    }
  };

  const toggleTicketExpanded = (ticketId: string) => {
    let shouldLoad = false;
    setExpandedTicketIds((prev) => {
      const next = new Set(prev);
      if (next.has(ticketId)) {
        next.delete(ticketId);
      } else {
        next.add(ticketId);
        shouldLoad = true;
      }
      return next;
    });

    if (shouldLoad && !ticketDetailsById[ticketId] && !loadingDetailIds.has(ticketId)) {
      void loadTicketDetail(ticketId);
    }
  };

  const handleCreateTicket = async () => {
    if (!createTitle.trim()) {
      toast.error(t("account.ticketTitleRequired"));
      return;
    }
    if (!createMessage.trim() && createAttachments.length === 0) {
      toast.error(t("account.ticketMessageOrAttachmentRequired"));
      return;
    }
    setIsSaving(true);
    try {
      const created = await ticketService.createMyTicket(
        {
          title: createTitle.trim(),
          type: createType,
          message: createMessage.trim() || undefined,
        },
        createAttachments
      );
      toast.success(t("account.ticketCreated"));
      setCreateTitle("");
      setCreateType("BUG");
      setCreateMessage("");
      setCreateAttachments([]);
      setIsCreateExpanded(false);
      setExpandedTicketIds((prev) => {
        const next = new Set(prev);
        next.add(created.id);
        return next;
      });
      setTicketDetailsById((prev) => ({
        ...prev,
        [created.id]: created,
      }));
      await loadTickets(statusFilter);
    } catch (error: any) {
      toast.error(error?.response?.data?.message ?? t("common.genericError"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleReply = async (ticketId: string) => {
    const detail = ticketDetailsById[ticketId];
    if (!detail) return;

    const draft = getReplyDraft(ticketId);
    if (!draft.message.trim() && draft.attachments.length === 0) {
      toast.error(t("account.ticketMessageOrAttachmentRequired"));
      return;
    }
    setIsSaving(true);
    try {
      const updated = await ticketService.createMyTicketMessage(
        detail.id,
        { message: draft.message.trim() || undefined },
        draft.attachments
      );
      setTicketDetailsById((prev) => ({
        ...prev,
        [detail.id]: updated,
      }));
      setReplyDraftsByTicketId((prev) => ({
        ...prev,
        [ticketId]: { ...EMPTY_REPLY_DRAFT },
      }));
      setExpandedMessageIds(new Set());
      await loadTickets(statusFilter);
    } catch (error: any) {
      toast.error(error?.response?.data?.message ?? t("common.genericError"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseTicket = async (ticketId: string) => {
    const detail = ticketDetailsById[ticketId];
    if (!detail) return;

    setIsSaving(true);
    try {
      const updated = await ticketService.updateMyTicketStatus(detail.id, {
        status: "CLOSED",
      });
      setTicketDetailsById((prev) => ({
        ...prev,
        [detail.id]: updated,
      }));
      setExpandedMessageIds(new Set());
      await loadTickets(statusFilter);
      toast.success(t("account.ticketClosed"));
    } catch (error: any) {
      toast.error(error?.response?.data?.message ?? t("common.genericError"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleAdminNavigate = () => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem("admin-tab", "tickets");
    }
    navigate("admin");
  };

  return (
    <Card className="stat-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquareWarning className="h-5 w-5" style={iconStyle} />
          {t("account.tickets")}
        </CardTitle>
        <CardDescription>{t("account.ticketsDescription")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isAdmin && (
          <div
            className="flex flex-col gap-2 rounded-lg border border-border bg-input-background px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
            style={{ backgroundColor: "var(--input-background)" }}
          >
            <div className="space-y-1">
              <p className="text-sm font-medium" style={primaryTextStyle}>
                {t("account.ticketAdminLink")}
              </p>
              <p className="text-xs" style={secondaryTextStyle}>
                {t("account.ticketAdminDescription")}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleAdminNavigate}
              className="bg-background hover:bg-background dark:bg-background dark:hover:bg-background"
            >
              {t("account.ticketAdminCta")}
            </Button>
          </div>
        )}

        <div className="space-y-3 rounded-lg border border-border p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h4 className="font-semibold">{t("account.ticketCreateTitle")}</h4>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsCreateExpanded((prev) => !prev)}
            >
              {isCreateExpanded ? t("common.close") : t("account.ticketCreateAction")}
            </Button>
          </div>

          {isCreateExpanded && (
            <>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-2 md:col-span-2">
                  <Label>{t("account.ticketTitleLabel")}</Label>
                  <Input
                    value={createTitle}
                    maxLength={255}
                    onChange={(event) => setCreateTitle(event.target.value)}
                    placeholder={t("account.ticketTitlePlaceholder")}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("account.ticketType")}</Label>
                  <Select value={createType} onValueChange={(value) => setCreateType(value as TicketType)}>
                    <SelectTrigger>
                      <SelectValue placeholder={createType} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BUG">
                        <span className="inline-flex items-center gap-2">
                          <Bug className="h-4 w-4" />
                          {t("account.ticketTypeBUG")}
                        </span>
                      </SelectItem>
                      <SelectItem value="FEATURE">
                        <span className="inline-flex items-center gap-2">
                          <Lightbulb className="h-4 w-4" />
                          {t("account.ticketTypeFEATURE")}
                        </span>
                      </SelectItem>
                      <SelectItem value="FEEDBACK">
                        <span className="inline-flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4" />
                          {t("account.ticketTypeFEEDBACK")}
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t("account.ticketAttachments")}</Label>
                  <Input
                    type="file"
                    accept="image/png,image/jpeg,image/gif"
                    multiple
                    onChange={(event) => {
                      handleCreateAttachments(event.target.files);
                      event.currentTarget.value = "";
                      keepScrollPositionAfterFileSelection();
                    }}
                  />
                  {createAttachments.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-xs text-secondary">
                          {createAttachments.length} {t("account.ticketFilesSelected")}
                        </p>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2"
                          onClick={clearCreateAttachments}
                        >
                          {t("common.delete")}
                        </Button>
                      </div>
                      <div className="space-y-1">
                        {createAttachments.map((file, index) => (
                          <div
                            key={toAttachmentKey(file)}
                            className="flex flex-wrap items-center gap-2 text-xs"
                          >
                            <span className="min-w-0 flex-1 break-all">{file.name}</span>
                            <span className="text-secondary">{formatBytes(file.size)}</span>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-7 px-2"
                              onClick={() => removeCreateAttachment(index)}
                            >
                              {t("common.delete")}
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>{t("account.ticketMessage")}</Label>
                  <Textarea
                    value={createMessage}
                    maxLength={5000}
                    onChange={(event) => setCreateMessage(event.target.value)}
                    placeholder={t("account.ticketMessagePlaceholder")}
                  />
                </div>
              </div>
              <Button className="btn-primary" disabled={isSaving} onClick={handleCreateTicket}>
                {t("account.ticketCreateAction")}
              </Button>
            </>
          )}
        </div>

        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h4 className="font-semibold">{t("account.ticketInboxTitle")}</h4>
            <Select
              value={statusFilter}
              onValueChange={(value) => setStatusFilter(value as TicketStatus | "ALL")}
            >
              <SelectTrigger className="w-full sm:w-[220px]">
                <SelectValue placeholder={statusFilter} />
              </SelectTrigger>
              <SelectContent>
                {STATUS_FILTERS.map((value) => (
                  <SelectItem key={value} value={value}>
                    {value === "ALL" ? t("account.ticketFilterAll") : t(`account.ticketStatus${value}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isLoadingList ? (
            <p className="text-sm text-secondary">{t("common.loading")}</p>
          ) : tickets.length === 0 ? (
            <p className="text-sm text-secondary">{t("account.ticketListEmpty")}</p>
          ) : (
            <div className="space-y-3">
              {tickets.map((ticket) => {
                const isExpanded = expandedTicketIds.has(ticket.id);
                const detail = ticketDetailsById[ticket.id];
                const isDetailLoading = loadingDetailIds.has(ticket.id);
                const replyDraft = getReplyDraft(ticket.id);

                return (
                  <div key={ticket.id} className="rounded-lg border border-border p-3">
                    <button
                      type="button"
                      className="w-full rounded-md text-left transition hover:bg-accent/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                      onClick={() => toggleTicketExpanded(ticket.id)}
                      aria-expanded={isExpanded}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex min-w-0 items-start justify-between gap-2">
                            <p className="min-w-0 flex-1 break-words font-medium leading-tight">
                              {ticket.title}
                          </p>
                          <div className="flex flex-wrap items-center justify-end gap-2">
                            <Badge className={`${ticketTypeBadgeClass(ticket.type)} ${TICKET_META_BADGE_CLASS}`} variant="outline">
                              {(() => {
                                const Icon = ticketTypeIcon(ticket.type);
                                return (
                                  <span className="inline-flex items-center gap-1">
                                    <Icon className="h-3.5 w-3.5" />
                                    {t(`account.ticketType${ticket.type}`)}
                                  </span>
                                );
                              })()}
                            </Badge>
                            <Badge className={TICKET_META_BADGE_CLASS} variant={statusBadgeVariant(ticket.status)}>
                              {t(`account.ticketStatus${ticket.status}`)}
                            </Badge>
                          </div>
                        </div>
                        <p className="mt-1 break-words text-xs text-secondary [overflow-wrap:anywhere]">
                          {localizeTicketMessageText(ticket.lastMessagePreview, t)}
                        </p>
                        <p className="mt-2 text-xs text-secondary">
                          {ticket.messageCount} {t("account.ticketMessagesShort")} ·{" "}
                            {ticket.attachmentCount} {t("account.ticketAttachmentsShort")} ·{" "}
                            {formatBytes(ticket.attachmentsBytesTotal)}
                          </p>
                        </div>
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="mt-3 rounded-lg border border-border p-3">
                        {isDetailLoading || !detail ? (
                          <p className="text-sm text-secondary">{t("common.loading")}</p>
                        ) : (
                          <div className="space-y-4">
                            {detail.status === "CLOSED" && detail.reopenUntil && (
                              <p className="break-words text-xs text-secondary">
                                {t("account.ticketReopenUntil")}: {new Date(detail.reopenUntil).toLocaleString()}
                              </p>
                            )}

                            <div className="max-h-[320px] space-y-3 overflow-x-hidden overflow-y-auto pr-1">
                              {detail.messages.map((message) => {
                                const localizedMessage = localizeTicketMessageText(message.message, t);
                                if (isSystemMessage(message)) {
                                  return (
                                    <div key={message.id} className="flex items-center gap-3 py-1">
                                      <span className="h-px flex-1 bg-border" />
                                      <p className="max-w-[80%] break-words text-center text-xs font-medium text-secondary [overflow-wrap:anywhere]">
                                        {localizedMessage || t("account.messagesFromSystem")}
                                      </p>
                                      <span className="h-px flex-1 bg-border" />
                                    </div>
                                  );
                                }
                                return (
                                  <div
                                    key={message.id}
                                    className={`flex ${isOwnMessage(message) ? "justify-end" : "justify-start"}`}
                                  >
                                    <div
                                      className={`min-w-0 w-fit max-w-full rounded-lg border p-3 ${
                                        isOwnMessage(message)
                                          ? "border-primary/40 bg-primary/5"
                                          : "border-border bg-background"
                                      }`}
                                      style={{ maxWidth: MESSAGE_BUBBLE_MAX_WIDTH }}
                                    >
                                      <div
                                        className={`flex min-w-0 flex-wrap items-center gap-2 ${
                                          isOwnMessage(message) ? "justify-end text-right" : "justify-between"
                                        }`}
                                      >
                                        <p className="min-w-0 break-words text-sm font-medium [overflow-wrap:anywhere]">
                                          {message.senderUsername ??
                                            (message.senderType === "SYSTEM"
                                              ? t("account.messagesFromSystem")
                                              : message.senderType)}
                                        </p>
                                        <p className="text-xs text-secondary">
                                          {new Date(message.createdAt).toLocaleString()}
                                        </p>
                                      </div>
                                      {localizedMessage && (
                                        <>
                                          <p className="mt-2 whitespace-pre-wrap break-all text-sm [overflow-wrap:anywhere] [word-break:break-word]">
                                            {expandedMessageIds.has(message.id)
                                              ? localizedMessage
                                              : toCollapsedMessage(localizedMessage)}
                                          </p>
                                          {localizedMessage.length > MESSAGE_COLLAPSE_LENGTH && (
                                            <Button
                                              type="button"
                                              size="sm"
                                              variant="ghost"
                                              className="mt-1 h-7 px-2"
                                              onClick={() => toggleMessageExpanded(message.id)}
                                            >
                                              {expandedMessageIds.has(message.id)
                                                ? t("common.showLess")
                                                : t("common.showMore")}
                                            </Button>
                                          )}
                                        </>
                                      )}
                                      {message.attachments.length > 0 && (
                                        <div className="mt-2 grid gap-2 sm:grid-cols-2">
                                          {message.attachments.map((attachment) => (
                                            <TicketAttachmentPreview
                                              key={attachment.id}
                                              ticketId={detail.id}
                                              attachment={attachment}
                                              fetchBlob={ticketService.getMyAttachmentBlob}
                                              onOpen={() =>
                                                ticketService.openMyAttachment(detail.id, attachment.id)
                                              }
                                              t={t}
                                            />
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>

                            <div className="space-y-3 rounded-lg border border-border p-3">
                              <h6 className="font-medium">{t("account.ticketReplyTitle")}</h6>
                              <Textarea
                                value={replyDraft.message}
                                maxLength={5000}
                                onChange={(event) => setReplyMessage(ticket.id, event.target.value)}
                                placeholder={t("account.ticketReplyPlaceholder")}
                              />
                              <Input
                                type="file"
                                accept="image/png,image/jpeg,image/gif"
                                multiple
                                onChange={(event) => {
                                  handleReplyAttachments(ticket.id, event.target.files);
                                  event.currentTarget.value = "";
                                  keepScrollPositionAfterFileSelection();
                                }}
                              />
                              {replyDraft.attachments.length > 0 && (
                                <div className="space-y-2">
                                  <div className="flex flex-wrap items-center justify-between gap-2">
                                    <p className="text-xs text-secondary">
                                      {replyDraft.attachments.length} {t("account.ticketFilesSelected")}
                                    </p>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 px-2"
                                      onClick={() => clearReplyAttachments(ticket.id)}
                                    >
                                      {t("common.delete")}
                                    </Button>
                                  </div>
                                  <div className="space-y-1">
                                    {replyDraft.attachments.map((file, index) => (
                                      <div
                                        key={toAttachmentKey(file)}
                                        className="flex flex-wrap items-center gap-2 text-xs"
                                      >
                                        <span className="min-w-0 flex-1 break-all">{file.name}</span>
                                        <span className="text-secondary">{formatBytes(file.size)}</span>
                                        <Button
                                          type="button"
                                          size="sm"
                                          variant="outline"
                                          className="h-7 px-2"
                                          onClick={() => removeReplyAttachment(ticket.id, index)}
                                        >
                                          {t("common.delete")}
                                        </Button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
                                <Button
                                  className="btn-primary w-full sm:w-auto"
                                  disabled={isSaving || !canReplyToTicket(detail)}
                                  onClick={() => void handleReply(ticket.id)}
                                >
                                  {t("account.ticketReplyAction")}
                                </Button>
                              </div>
                            </div>

                            <div className="flex flex-wrap justify-end gap-2">
                              <Button
                                variant="outline"
                                onClick={() => {
                                  void refreshTicket(ticket.id);
                                }}
                              >
                                {t("admin.systemLogsRefresh")}
                              </Button>
                              {canCloseTicket(detail) && (
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="outline" disabled={isSaving}>
                                      {t("account.ticketCloseAction")}
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>{t("account.ticketCloseConfirmTitle")}</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        {t("account.ticketCloseConfirmDescription")}
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => {
                                          void handleCloseTicket(ticket.id);
                                        }}
                                      >
                                        {t("account.ticketCloseConfirmAction")}
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
